import { API_BASE_URL } from '../config';
const API_VERSION = '1.0.2'; // Version bump to invalidate cache

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiResponse<T> {
  data: T;
  status: number;
}

class ApiClient {
  private baseUrl: string;
  private rememberInFlight: Promise<boolean> | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    contentType: string = 'application/ld+json'
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    const { headers: optionHeaders, ...restOptions } = options;

    const config: RequestInit = {
      credentials: 'include',
      ...restOptions,
      headers: {
        'Content-Type': contentType,
        'Accept': 'application/ld+json',
        ...(optionHeaders as Record<string, string> || {}),
      },
    };

    try {
      let response = await fetch(url, config);

      if (response.status === 401 && this.shouldAttemptRemember(endpoint)) {
        const restored = await this.tryRestoreSession();
        if (restored) {
          response = await fetch(url, config);
        }
      }
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new ApiError(response.status, `HTTP ${response.status}: ${errorText}`);
      }

      // Handle empty responses (204 No Content, or any response with no body)
      let data: T | null = null;
      if (response.status !== 204) {
        const text = await response.text();
        if (text) {
          data = JSON.parse(text) as T;
        }
      }
      return {
        data: data as T,
        status: response.status,
      };
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, `Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data: any, options: RequestInit = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options,
    }, 'application/ld+json');
  }

  async patch<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }, 'application/merge-patch+json');
  }

  async put<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, 'application/json');
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async downloadFile(endpoint: string, filename: string): Promise<void> {
    const url = `${this.baseUrl}${endpoint}`;
    let response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
    });

    if (response.status === 401 && this.shouldAttemptRemember(endpoint)) {
      const restored = await this.tryRestoreSession();
      if (restored) {
        response = await fetch(url, {
          method: 'GET',
          credentials: 'include',
        });
      }
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(response.status, `HTTP ${response.status}: ${errorText}`);
    }

    const text = await response.text();
    const bom = '\uFEFF';
    const content = text.startsWith(bom) ? text : bom + text;
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(objectUrl);
  }

  private shouldAttemptRemember(endpoint: string): boolean {
    return endpoint !== '/api/auth/login'
      && endpoint !== '/api/auth/logout'
      && endpoint !== '/api/auth/remember';
  }

  private async tryRestoreSession(): Promise<boolean> {
    if (!this.rememberInFlight) {
      this.rememberInFlight = (async () => {
        try {
          const response = await fetch(`${this.baseUrl}/api/auth/remember`, {
            method: 'POST',
            credentials: 'include',
          });

          return response.ok;
        } catch (error) {
          return false;
        } finally {
          this.rememberInFlight = null;
        }
      })();
    }

    return this.rememberInFlight;
  }
}

export const apiClient = new ApiClient(API_BASE_URL);

// Helper to convert image URLs - handles both relative paths and old localhost URLs
export function getImageUrl(url: string | null | undefined): string {
  if (!url) return '';

  // If it's a relative path, prepend the API base URL
  if (url.startsWith('/')) {
    return `${API_BASE_URL}${url}`;
  }

  // If it's an old localhost URL, replace with current hostname
  if (url.includes('localhost:8010') || url.includes('localhost:8000') || url.includes('localhost/')) {
    const path = url.replace(/^https?:\/\/localhost(:\d+)?/, '');
    return `${API_BASE_URL}${path}`;
  }

  // Return as-is for other absolute URLs
  return url;
}
